import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowClockwise, Warning, FileArrowDown,
  Trash, MagnifyingGlass, CircleNotch, Database, FilePdf,
} from "@phosphor-icons/react";
import {
  getNmcRawIngests, getNmcRawSummary, getNmcRawRows, nmcRawRowsCsvUrl, nmcResetYear, getNmcValidation,
} from "../lib/api";

// Small helpers
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));
const fmtDT = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return s; }
};
// "13.07.2026" → "13 Jul 2026" (mirrors CollegeStatsRail formatter)
const fmtPubDate = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (!m) return s;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const idx = parseInt(m[2], 10) - 1;
  const yyyy = m[3].length === 2 ? `20${m[3]}` : m[3];
  return `${m[1].padStart(2, "0")} ${months[idx] || m[2]} ${yyyy}`;
};

const STATUS_STYLE = {
  running: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
  needs_hint: "bg-violet-100 text-violet-700",
};

// ─── Reset-Year confirmation modal ─────────────────────────────────────────
const ResetYearDialog = ({ open, onClose, onDone }) => {
  const [year, setYear] = useState(2026);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) { setPreview(null); setError(null); return; }
    setLoading(true);
    nmcResetYear(year, true).then((r) => setPreview(r.data)).catch((e) =>
      setError(e?.response?.data?.detail || e.message)
    ).finally(() => setLoading(false));
  }, [open, year]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="reset-year-dialog">
      <div className="bg-white border-2 border-[#09090B] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="border-b-2 border-[#09090B] px-5 py-3 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2A00] flex items-center gap-2">
            <Warning weight="fill" size={14} /> Reset NMC year — DESTRUCTIVE
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-[#09090B]" data-testid="reset-close">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block">
            Year
            <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value || "0", 10))}
              className="mt-1 block w-32 border border-zinc-300 px-2 py-1 font-mono text-sm"
              data-testid="reset-year-input" />
          </label>
          {loading && <div className="text-xs text-zinc-500 flex items-center gap-1"><CircleNotch className="animate-spin" size={12} /> Loading preview…</div>}
          {error && <div className="text-xs text-rose-600">{error}</div>}
          {preview && (
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs font-mono space-y-1" data-testid="reset-preview">
              <div>colleges with <code>nmc_data.seats.{year}</code>: <b>{preview.colleges_with_seats}</b> (will be $unset)</div>
              <div>NMC_{year}_* placeholders: <b>{preview.placeholders}</b> (will be deleted)</div>
              <div>college_yearly_stats rows: <b>{preview.yearly_stats}</b> (will be deleted)</div>
              <div>raw_seat_matrix rows: <b>{preview.raw_rows}</b> (will be deleted)</div>
              <div>ingest_runs: <b>{preview.ingest_runs}</b> (will be deleted)</div>
            </div>
          )}
          <div className="text-[11px] text-zinc-500 leading-relaxed border-l-2 border-[#FF2A00] pl-3">
            This clears every trace of NMC year <b>{year}</b> so you can re-ingest cleanly.
            Master college records (aliases, codes, etc.) are preserved — only the year-specific
            seat data is removed. Cannot be undone.
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="h-9 px-4 border border-zinc-300 text-xs font-bold uppercase" data-testid="reset-cancel">Cancel</button>
            <button
              disabled={applying || !preview}
              onClick={async () => {
                setApplying(true);
                try {
                  const r = await nmcResetYear(year, false);
                  onDone?.(r.data);
                  onClose();
                } catch (e) { setError(e?.response?.data?.detail || e.message); }
                finally { setApplying(false); }
              }}
              className="h-9 px-4 bg-[#FF2A00] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40"
              data-testid="reset-confirm">
              {applying ? "Resetting…" : `Reset year ${year}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Ingest detail (rows) — READ-ONLY ───────────────────────────────────────
// This screen is a read-only audit view of the raw NMC seat-matrix ingest.
// Matching happens once at ingest time (ingestion_common.resolve_colleges, shared
// with MCC/state). Unmatched colleges are resolved in the unified Ingest Recon
// Viewer, opened from the Ingestion Diary ("SM Reconcile" on the NMC row) — NOT here.
// The bucket chips below are pure filters over the ingest-time match_method.
const METHOD_ORDER = ["exact_code", "exact_name", "fuzzy", "ambiguous", "unmatched", "new_establishment", "manual", "uncomputed"];
const METHOD_LABEL = {
  exact_code: "Code", exact_name: "Exact name", fuzzy: "Fuzzy", ambiguous: "Ambiguous",
  unmatched: "Unmatched", new_establishment: "New est", manual: "Manual", uncomputed: "Uncomputed",
};
const methodLabel = (m) => METHOD_LABEL[m] || m;

const IngestDetail = ({ ingestId, onBack, onSummaryLoaded }) => {
  const [summary, setSummary] = useState(null);
  const [validation, setValidation] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [filter, setFilter] = useState({ state: "", is_new: null, unmatched_only: false, increased_only: false, collisions_only: false, match_method: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [collisionMap, setCollisionMap] = useState({});

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getNmcRawSummary(ingestId).then((r) => r.data).catch(() => null),
      getNmcValidation(ingestId).then((r) => r.data).catch(() => null),
    ]).then(([s, v]) => {
      setSummary(s);
      setValidation(v);
      if (s) onSummaryLoaded?.(s);
    }).finally(() => setLoading(false));
  }, [ingestId, onSummaryLoaded]);

  useEffect(() => {
    getNmcRawRows(ingestId, {
      page, page_size: pageSize,
      ...(filter.state ? { state: filter.state } : {}),
      ...(filter.is_new === true ? { is_new: true } : filter.is_new === false ? { is_new: false } : {}),
      ...(filter.unmatched_only ? { unmatched_only: true } : {}),
      ...(filter.increased_only ? { increased_only: true } : {}),
      ...(filter.collisions_only ? { collisions_only: true } : {}),
      ...(filter.match_method ? { match_method: filter.match_method } : {}),
      ...(filter.q ? { q: filter.q } : {}),
    }).then((r) => {
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setCollisionMap(r.data.collision_map || {});
    }).catch(() => { setRows([]); setTotal(0); });
  }, [ingestId, page, pageSize, filter]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const colSpanAll = 10;
  const grand = summary?.grand || {};
  const byMethod = summary?.by_method || {};
  const methodKeys = Object.keys(byMethod).sort((a, b) => {
    const ia = METHOD_ORDER.indexOf(a), ib = METHOD_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const toggleMethod = (m) => {
    setPage(1);
    setFilter((f) => ({ ...f, match_method: f.match_method === m ? "" : m }));
  };

  return (
    <div className="space-y-4" data-testid="ingest-detail">
      <button onClick={onBack} className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF] hover:underline inline-flex items-center gap-1" data-testid="detail-back">
        <ArrowLeft size={12} weight="bold" /> Back to all runs
      </button>

      {loading ? <div className="text-sm text-zinc-500">Loading summary…</div> : summary && (
        <div className="border-2 border-zinc-200 bg-white p-4" data-testid="ingest-summary">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Ingest</div>
              <div className="text-sm font-mono">{ingestId}</div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Year {summary.run?.year} · Started {fmtDT(summary.run?.started_at)}
                {summary.run?.finished_at ? ` · Finished ${fmtDT(summary.run.finished_at)}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[summary.run?.status] || "bg-zinc-100 text-zinc-600"}`} data-testid="ingest-status">
                {summary.run?.status}
              </span>
              <a href={nmcRawRowsCsvUrl(ingestId)} className="h-8 px-3 border border-zinc-300 text-[10px] font-bold uppercase tracking-wider text-[#09090B] hover:border-[#6043EF] hover:text-[#6043EF] transition-colors inline-flex items-center gap-1" data-testid="csv-export">
                <FileArrowDown size={12} weight="bold" /> Export CSV
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
            <Stat label="Total rows" value={grand.rows} testid="stat-rows" />
            <Stat label="New-est marker" value={grand.new_est_marker} accent="orange" testid="stat-newmarker" />
            <Stat label="Sr.No range" value={`${grand.min_sr_no ?? "—"} → ${grand.max_sr_no ?? "—"}`} testid="stat-srrange" />
            <Stat label="Renewed Σ" value={grand.renewed} testid="stat-renewed" />
            <Stat label="Increased Σ" value={grand.increased} testid="stat-increased" />
            <Stat label="Total seats Σ" value={grand.total_seats} accent="violet" testid="stat-totalseats" />
            <Stat label="Unmatched" value={grand.unmatched} accent={grand.unmatched > 0 ? "rose" : ""} testid="stat-unmatched" />
          </div>
          {summary.run?.notes && (
            <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
              <b>Note:</b> {summary.run.notes}
            </div>
          )}
          {grand.unmatched > 0 && (
            <div className="mt-3 text-[11px] text-[#6043EF] bg-[#6043EF]/5 border border-[#6043EF]/30 px-3 py-2" data-testid="reconcile-pointer">
              <b>{fmt(grand.unmatched)} unmatched</b> college(s) — resolve them in the unified reconciler:
              open <b>Ingestion Diary</b> → the NMC seat-matrix row → <b>SM Reconcile</b> (state-wise LLM match, manual search, treat-as-new). This page is read-only.
            </div>
          )}
        </div>
      )}

      {/* Step-2 validation panel */}
      {validation && validation.validation && (validation.validation.checks || []).length > 0 && (
        <div className="border-2 border-zinc-200 bg-white p-4" data-testid="validation-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Step 2 · Validation</div>
              <div className="text-sm font-mono mt-0.5">
                {validation.validation.status === "needs_hint"
                  ? <span className="text-rose-600 font-bold">✖ Blocked — needs operator hint</span>
                  : <span className="text-emerald-600 font-bold">✓ All checks passed</span>}
                {validation.validation.main_pages?.length > 0 && (
                  <span className="ml-3 text-zinc-500">
                    Main-table pages: {validation.validation.main_pages[0]}–{validation.validation.main_pages.slice(-1)[0]}
                    ({validation.validation.main_pages.length} pages)
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="grid gap-2">
            {validation.validation.checks.map((c, i) => (
              <div key={i} className={`border px-3 py-2 text-xs flex items-start gap-2 ${c.severity === "block" ? "border-rose-200 bg-rose-50" : c.severity === "warn" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50/50"}`} data-testid={`check-${c.name}`}>
                <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider shrink-0 ${c.severity === "block" ? "bg-rose-500 text-white" : c.severity === "warn" ? "bg-amber-500 text-white" : "bg-emerald-500 text-white"}`}>
                  {c.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#09090B]">{c.name}</div>
                  <div className="text-zinc-600 mt-0.5">{c.message}</div>
                  {c.details && Object.keys(c.details).length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-[#6043EF]">details</summary>
                      <pre className="mt-1 text-[10px] font-mono bg-white/60 border border-zinc-200 p-2 overflow-x-auto max-h-40">{JSON.stringify(c.details, null, 2)}</pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
          {validation.validation.status === "needs_hint" && (
            <div className="mt-3 text-[11px] text-rose-700 bg-rose-50 border-l-2 border-rose-500 pl-3 py-2">
              Master upsert was <b>halted</b>. Add a <b>table parsing hint</b> in <a href="/admin/crawlers" className="underline">Admin → Crawlers</a> (below the NMC document row) and re-run "Re-ingest (Step 2)".
            </div>
          )}
        </div>
      )}

      {/* Match-method buckets — read-only filters over the ingest-time match_method */}
      {summary && methodKeys.length > 0 && (
        <div className="border border-zinc-200 bg-white p-3" data-testid="method-buckets">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Match method — filter</div>
          <div className="flex flex-wrap items-center gap-2">
            {methodKeys.map((m) => {
              const active = filter.match_method === m;
              return (
                <button key={m} onClick={() => toggleMethod(m)}
                  className={`text-[11px] flex items-center gap-1.5 px-2.5 py-1.5 border transition-colors ${active ? "border-[#6043EF] bg-[#6043EF]/10 text-[#6043EF]" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                  data-testid={`method-bucket-${m}`}>
                  <span className="font-bold uppercase tracking-wider">{methodLabel(m)}</span>
                  <span className="font-mono">{byMethod[m]}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="border border-zinc-200 bg-white p-3 flex flex-wrap items-center gap-2" data-testid="ingest-filters">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input type="search" placeholder="Search name / code"
            value={filter.q} onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, q: e.target.value })); }}
            className="h-8 pl-7 pr-2 border border-zinc-300 text-xs w-56" data-testid="filter-q" />
        </div>
        <input type="text" placeholder="State (exact)" value={filter.state}
          onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, state: e.target.value })); }}
          className="h-8 px-2 border border-zinc-300 text-xs w-40" data-testid="filter-state" />
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={filter.is_new === true}
            onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, is_new: e.target.checked ? true : null })); }}
            data-testid="filter-newonly" /> New-est only
        </label>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={filter.unmatched_only}
            onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, unmatched_only: e.target.checked })); }}
            data-testid="filter-unmatched" /> Unmatched only
        </label>
        <label className="text-xs flex items-center gap-1" title="Colleges with seats_increased > 0 AND not flagged new-establishment — mirrors the homepage 'colleges expanded' stat">
          <input type="checkbox" checked={filter.increased_only}
            onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, increased_only: e.target.checked })); }}
            data-testid="filter-increased" /> Intake +
        </label>
        <label className="text-xs flex items-center gap-1 font-bold text-rose-600" title="Rows whose resolved college is currently a matching-bug collision (>=2 distinct raw NMC codes landed on the same college_code) — same detector as the Data Health 'NMC code collisions' check, scoped to this ingest. Read-only view; collisions are prevented by key_registry and surfaced in the reconciler.">
          <input type="checkbox" checked={filter.collisions_only}
            onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, collisions_only: e.target.checked })); }}
            data-testid="filter-collisions" /> Collisions{filter.collisions_only && Object.keys(collisionMap).length > 0 ? ` (${Object.keys(collisionMap).length})` : ""}
        </label>
        <div className="ml-auto text-[11px] text-zinc-500 font-mono">
          {fmt(total)} rows · page {page}/{totalPages}
        </div>
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
          className="h-8 px-2 border border-zinc-300 text-xs disabled:opacity-40" data-testid="page-prev">Prev</button>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
          className="h-8 px-2 border border-zinc-300 text-xs disabled:opacity-40" data-testid="page-next">Next</button>
      </div>

      {/* Rows */}
      <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="rows-table">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 border-b-2 border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-2 py-2 text-left w-10">Pg</th>
              <th className="px-2 py-2 text-left w-14">Sr</th>
              <th className="px-2 py-2 text-left w-24">State</th>
              <th className="px-2 py-2 text-left w-32">Code raw</th>
              <th className="px-2 py-2 text-left">College name</th>
              <th className="px-2 py-2 text-left w-20">Mgmt</th>
              <th className="px-2 py-2 text-right w-16">Renewed</th>
              <th className="px-2 py-2 text-right w-16">Incr</th>
              <th className="px-2 py-2 text-right w-16">Total</th>
              <th className="px-2 py-2 text-left w-40">Matched to</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const prev = i > 0 ? rows[i - 1] : null;
              const pageBreak = !filter.collisions_only && prev && prev.page_no !== r.page_no;
              const collisionBreak = filter.collisions_only && (!prev || prev.match_college_code !== r.match_college_code);
              return (
                <Fragment key={i}>
                  {pageBreak && (
                    <tr className="bg-violet-50/70 border-y border-violet-200" data-testid={`page-break-${r.page_no}`}>
                      <td colSpan={colSpanAll} className="px-2 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#6043EF]">
                        ── page {r.page_no} ──
                      </td>
                    </tr>
                  )}
                  {collisionBreak && (
                    <tr className="bg-rose-50 border-y border-rose-200" data-testid={`collision-break-${r.match_college_code}`}>
                      <td colSpan={colSpanAll} className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">
                        <a href={`/colleges/${encodeURIComponent(r.match_college_code)}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {r.match_college_code}
                        </a>
                        {" "}— {collisionMap[r.match_college_code] ?? "?"} distinct NMC raw codes landed here (matching bug)
                      </td>
                    </tr>
                  )}
                  <tr className={`border-b border-zinc-100 ${r.is_new_establishment_marker ? "bg-orange-50/40" : filter.collisions_only ? "bg-rose-50/20" : ""}`} data-testid={`row-${i}`}>
                    <td className="px-2 py-1.5 font-mono text-zinc-500">{r.page_no}</td>
                    <td className="px-2 py-1.5 font-mono">{r.sr_no ?? "—"}</td>
                    <td className="px-2 py-1.5 truncate max-w-[7rem]" title={r.state}>{r.state || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {r.is_new_establishment_marker ? (
                        <span className="inline-block px-1.5 py-0.5 bg-[#FF2A00] text-white uppercase tracking-wider font-bold">New Est</span>
                      ) : (r.college_code_raw || "—")}
                    </td>
                    <td className="px-2 py-1.5">{r.college_name || <span className="text-rose-500 italic">(missing)</span>}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{r.management || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.seats_renewed ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.seats_increased ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{r.total_seats ?? "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {r.match_college_code ? (
                        <a href={`/colleges/${encodeURIComponent(r.match_college_code)}`}
                           target="_blank" rel="noopener noreferrer"
                           className="text-emerald-700 hover:text-emerald-900 hover:underline inline-flex items-center gap-1"
                           data-testid={`match-link-${i}`}
                           title={`Method: ${r.match_method || "—"} — click to open detail page`}>
                          {r.match_college_code}
                          {r.match_method && (
                            <span className="text-[8px] uppercase tracking-wider text-zinc-400 font-bold">{methodLabel(r.match_method)}</span>
                          )}
                        </a>
                      ) : (
                        <span className="text-zinc-400 inline-flex items-center gap-1" title="Unmatched — resolve in the Ingestion Diary reconciler">
                          — <span className="text-[8px] uppercase tracking-wider text-rose-400 font-bold">unmatched</span>
                        </span>
                      )}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={colSpanAll} className="px-4 py-12 text-center text-zinc-400 text-xs">{filter.collisions_only ? "No collisions — every raw NMC code maps to a unique college_code. ✓" : "No rows."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Per-state roll-up */}
      {summary?.by_state?.length > 0 && (
        <div className="border border-zinc-200 bg-white p-3" data-testid="by-state-table">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Per-state roll-up</div>
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-2 py-1.5 text-left w-10">#</th>
                <th className="px-2 py-1.5 text-left">State</th>
                <th className="px-2 py-1.5 text-right">Rows</th>
                <th className="px-2 py-1.5 text-right">New-est</th>
                <th className="px-2 py-1.5 text-right">Renewed Σ</th>
                <th className="px-2 py-1.5 text-right">Incr Σ</th>
                <th className="px-2 py-1.5 text-right">Total Σ</th>
                <th className="px-2 py-1.5 text-right">Unmatched</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_state.map((s, i) => (
                <tr key={s.state} className="border-b border-zinc-100">
                  <td className="px-2 py-1 font-mono text-[10px] text-zinc-400 tabular-nums">{i + 1}</td>
                  <td className="px-2 py-1 font-medium">{s.state || "—"}</td>
                  <td className="px-2 py-1 text-right font-mono">{s.rows}</td>
                  <td className="px-2 py-1 text-right font-mono">{s.new_est_marker || "—"}</td>
                  <td className="px-2 py-1 text-right font-mono">{s.renewed}</td>
                  <td className="px-2 py-1 text-right font-mono">{s.increased}</td>
                  <td className="px-2 py-1 text-right font-mono font-bold">{s.total_seats}</td>
                  <td className={`px-2 py-1 text-right font-mono ${s.unmatched > 0 ? "text-rose-600" : ""}`}>{s.unmatched}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent = "", testid }) => (
  <div className={`border border-zinc-200 px-3 py-2 ${accent === "violet" ? "bg-violet-50" : accent === "orange" ? "bg-orange-50" : accent === "rose" ? "bg-rose-50" : "bg-white"}`} data-testid={testid}>
    <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`font-mono font-bold text-sm mt-0.5 ${accent === "rose" ? "text-rose-700" : "text-[#09090B]"}`}>{typeof value === "number" ? fmt(value) : value}</div>
  </div>
);

// ─── Runs list ─────────────────────────────────────────────────────────────
export default function AdminNmcRaw() {
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const reload = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await getNmcRawIngests(year ? parseInt(year, 10) : undefined, 50);
      setRuns(r.data.runs || []);
    } catch (e) {
      setLoadError(e?.response?.status === 401 ? "Session expired — please sign in again." : (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year]);

  const grouped = useMemo(() => {
    const byYear = {};
    for (const r of runs) {
      const y = String(r.year);
      byYear[y] = byYear[y] || [];
      byYear[y].push(r);
    }
    return byYear;
  }, [runs]);

  const pubDateLabel = fmtPubDate(selectedSummary?.pub_date);
  const sourceUrl = selectedSummary?.source_url || selectedSummary?.run?.pdf_url;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-nmc-raw-page">
      {/* Header */}
      <section className="border-b border-zinc-200 py-6 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <Link to="/admin" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-2 hover:underline" data-testid="back-to-admin">
            <ArrowLeft size={12} weight="bold" /> Admin
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-1">STEP 1 · RAW SEAT MATRIX AUDIT</p>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B] font-heading flex items-center flex-wrap gap-x-2 gap-y-1" data-testid="page-heading">
                <Database weight="duotone" />
                <span>NMC — raw seat matrix</span>
                {selected && pubDateLabel && (
                  <>
                    <span className="text-zinc-400">—</span>
                    <span className="font-mono">{pubDateLabel}</span>
                  </>
                )}
                {selected && sourceUrl && (
                  <a href={sourceUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-[0.15em] text-[#6043EF] hover:underline"
                    data-testid="heading-source-pdf" title="Open source PDF">
                    <FilePdf size={14} weight="fill" /> · PDF
                  </a>
                )}
              </h1>
              <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
                Every row the LLM extracts from an NMC seat-matrix PDF lands here first — before dedup, matching or upsert.
                Pick a run to inspect page-by-page rows, cross-check against the source PDF, or export CSV.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={reload} className="h-9 px-3 border border-zinc-300 text-xs font-bold uppercase tracking-wider hover:border-[#6043EF] hover:text-[#6043EF] transition-colors inline-flex items-center gap-1" data-testid="reload-runs">
                <ArrowClockwise size={13} weight="bold" /> Reload
              </button>
              <button onClick={() => setResetOpen(true)} className="h-9 px-3 bg-[#FF2A00] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[#c72200] transition-colors" data-testid="open-reset">
                <Trash size={13} weight="bold" /> Reset year
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {selected ? (
          <IngestDetail
            ingestId={selected}
            onBack={() => { setSelected(null); setSelectedSummary(null); }}
            onSummaryLoaded={setSelectedSummary}
          />
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Filter year</label>
              <input type="number" placeholder="Any" value={year} onChange={(e) => setYear(e.target.value)}
                className="h-8 w-24 px-2 border border-zinc-300 text-xs font-mono" data-testid="filter-year" />
              <span className="text-[11px] text-zinc-500 ml-2 font-mono">{runs.length} run(s)</span>
            </div>

            {loading ? (
              <div className="py-16 text-center text-zinc-400 text-sm">Loading runs…</div>
            ) : loadError ? (
              <div className="border border-rose-300 bg-rose-50 py-8 px-4 text-center text-sm text-rose-700" data-testid="load-error">
                {loadError}
                <button onClick={reload} className="ml-3 underline font-bold" data-testid="retry-reload">Retry</button>
              </div>
            ) : runs.length === 0 ? (
              <div className="border border-dashed border-zinc-300 py-16 text-center text-sm text-zinc-500" data-testid="empty-runs">
                No ingest runs yet. Re-run the NMC ingest from the Admin Ingest tab and it will land here.
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(grouped).sort((a, b) => Number(b[0]) - Number(a[0])).map(([y, list]) => (
                  <div key={y}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-2">AY {y}-{String(Number(y) + 1).slice(-2)}</div>
                    <div className="border border-zinc-200 bg-white divide-y divide-zinc-100" data-testid={`runs-${y}`}>
                      {list.map((r) => (
                        <button key={r.ingest_id} onClick={() => setSelected(r.ingest_id)}
                          className="w-full text-left px-4 py-3 hover:bg-violet-50/40 flex items-center gap-4"
                          data-testid={`run-${r.ingest_id}`}>
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.status] || "bg-zinc-100 text-zinc-600"}`}>
                            {r.status}
                          </span>
                          <div className="font-mono text-xs text-zinc-700 truncate">{r.ingest_id}</div>
                          <div className="ml-auto text-[11px] text-zinc-500 font-mono flex items-center gap-4">
                            <span>{fmt(r.total_rows)} rows</span>
                            <span>{r.total_pages != null ? `${r.total_pages}p` : "—"}</span>
                            <span>{fmtDT(r.started_at)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <ResetYearDialog open={resetOpen} onClose={() => setResetOpen(false)} onDone={() => reload()} />
    </div>
  );
}
